import { useEffect, useState, createContext, useContext } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { FileText, Users, Package, FolderOpen, UserCog, LogOut, Plus, LayoutDashboard, ChevronsLeft, ChevronsRight, ShoppingCart, ClipboardList, Truck } from 'lucide-react';
import { qd, po } from './api';
import { ChangePassword } from './ChangePassword';
import './qd.css';
import QdLogin from './pages/QdLogin';
import QdDashboard from './pages/QdDashboard';
import QdQuotes from './pages/QdQuotes';
import QdQuoteEditor from './pages/QdQuoteEditor';
import QdClients from './pages/QdClients';
import QdCatalog from './pages/QdCatalog';
import QdDocuments from './pages/QdDocuments';
import QdAccounts from './pages/QdAccounts';
import PoOrders from './po/PoOrders';
import PoEditor from './po/PoEditor';
import PoTemplates from './po/PoTemplates';
import PoSupplier from './po/PoSupplier';

const QdAuthContext = createContext(null);
export const useQdAuth = () => useContext(QdAuthContext);

const NAV = [
  { to: '/quote/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/quote', end: true, icon: FileText, label: 'Quotes' },
  { to: '/quote/clients', icon: Users, label: 'Clients' },
  { to: '/quote/catalog', icon: Package, label: 'Catalog' },
  { to: '/quote/documents', icon: FolderOpen, label: 'Documents' },
];

const PO_NAV = [
  { to: '/quote/po', end: true, icon: ShoppingCart, label: 'Purchase Orders' },
  { to: '/quote/po/templates', icon: ClipboardList, label: 'Templates' },
  { to: '/quote/po/supplier', icon: Truck, label: 'Supplier & Catalogue' },
];

const Layout = ({ children }) => {
  const { user, logout, poAllowed } = useQdAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('qd_sidebar') === '1');
  const toggle = () => { const v = !collapsed; setCollapsed(v); localStorage.setItem('qd_sidebar', v ? '1' : '0'); };
  return (
    <div className="min-h-screen bg-slate-50 flex qd-app">
      <aside className={`${collapsed ? 'w-16' : 'w-56'} flex-shrink-0 bg-[#14233F] text-white flex flex-col no-print transition-[width] duration-200`}>
        <div className={`${collapsed ? 'px-0 justify-center' : 'px-5'} py-5 border-b border-white/10 flex items-center`}>
          {collapsed ? (
            <span className="font-bold text-teal-300">0g</span>
          ) : (
            <div>
              <p className="font-bold tracking-tight">0g Baltics</p>
              <p className="text-[11px] uppercase tracking-widest text-teal-300">Quote &amp; Docs</p>
            </div>
          )}
        </div>
        <button
          onClick={() => navigate('/quote/new')}
          className={`${collapsed ? 'mx-2' : 'mx-4'} mt-4 mb-2 flex items-center justify-center gap-2 bg-[#0F7D70] hover:bg-teal-600 text-white text-sm font-semibold py-2.5 rounded`}
          title="New quote"
          data-testid="qd-new-quote-btn"
        >
          <Plus className="h-4 w-4" /> {!collapsed && 'New quote'}
        </button>
        <nav className="flex-1 px-2 py-2 space-y-0.5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              title={n.label}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded text-sm ${collapsed ? 'justify-center px-0' : ''} ${isActive ? 'bg-white/10 text-white font-semibold' : 'text-slate-300 hover:bg-white/5'}`
              }
              data-testid={`qd-nav-${n.label.toLowerCase()}`}
            >
              <n.icon className="h-4 w-4 flex-shrink-0" /> {!collapsed && n.label}
            </NavLink>
          ))}
          {user.role === 'superadmin' && (
            <NavLink
              to="/quote/accounts"
              title="Accounts"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded text-sm ${collapsed ? 'justify-center px-0' : ''} ${isActive ? 'bg-white/10 text-white font-semibold' : 'text-slate-300 hover:bg-white/5'}`
              }
              data-testid="qd-nav-accounts"
            >
              <UserCog className="h-4 w-4 flex-shrink-0" /> {!collapsed && 'Accounts'}
            </NavLink>
          )}
          {poAllowed && (
            <>
              {!collapsed && <p className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-widest text-teal-300/70">Purchasing</p>}
              {collapsed && <div className="border-t border-white/10 my-2" />}
              {PO_NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  title={n.label}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded text-sm ${collapsed ? 'justify-center px-0' : ''} ${isActive ? 'bg-white/10 text-white font-semibold' : 'text-slate-300 hover:bg-white/5'}`
                  }
                  data-testid={`qd-nav-po-${n.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                >
                  <n.icon className="h-4 w-4 flex-shrink-0" /> {!collapsed && n.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <button onClick={toggle} title={collapsed ? 'Expand menu' : 'Collapse menu'}
          className="mx-2 mb-2 flex items-center justify-center gap-2 py-2 rounded text-xs text-slate-400 hover:text-white hover:bg-white/5"
          data-testid="qd-sidebar-toggle">
          {collapsed ? <ChevronsRight className="h-4 w-4" /> : <><ChevronsLeft className="h-4 w-4" /> Collapse</>}
        </button>
        {!collapsed && (
          <div className="px-4 py-4 border-t border-white/10">
            <p className="text-xs text-slate-300 truncate">{user.email}</p>
            <p className="text-[10px] uppercase tracking-wider text-teal-300 mb-2">{user.role}</p>
            <ChangePassword />
            <button onClick={logout} className="flex items-center gap-2 text-xs text-slate-400 hover:text-white" data-testid="qd-logout-btn">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        )}
        {collapsed && (
          <div className="py-4 border-t border-white/10 flex justify-center">
            <button onClick={logout} title="Sign out" className="text-slate-400 hover:text-white" data-testid="qd-logout-btn">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        )}
      </aside>
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
};

export default function QdApp() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [poAllowed, setPoAllowed] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('qd_token');
    if (!token) { setChecking(false); return; }
    qd.get('/auth/me')
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem('qd_token'))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!user) { setPoAllowed(false); return; }
    po.get('/me').then(() => setPoAllowed(true)).catch(() => setPoAllowed(false));
  }, [user]);

  const login = (token, u) => { localStorage.setItem('qd_token', token); setUser(u); };
  const logout = () => { localStorage.removeItem('qd_token'); setUser(null); window.location.href = '/quote/login'; };

  if (checking) return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Loading…</div>;

  return (
    <QdAuthContext.Provider value={{ user, login, logout, poAllowed }}>
      <Routes>
        <Route path="login" element={user ? <Navigate to="/quote" replace /> : <QdLogin />} />
        {!user ? (
          <Route path="*" element={<Navigate to="/quote/login" replace />} />
        ) : (
          <>
            <Route index element={<Layout><QdQuotes /></Layout>} />
            <Route path="dashboard" element={<Layout><QdDashboard /></Layout>} />
            <Route path="new" element={<Layout><QdQuoteEditor /></Layout>} />
            <Route path="devis/:id" element={<Layout><QdQuoteEditor /></Layout>} />
            <Route path="clients" element={<Layout><QdClients /></Layout>} />
            <Route path="catalog" element={<Layout><QdCatalog /></Layout>} />
            <Route path="documents" element={<Layout><QdDocuments /></Layout>} />
            <Route path="accounts" element={user.role === 'superadmin' ? <Layout><QdAccounts /></Layout> : <Navigate to="/quote" replace />} />
            <Route path="po" element={<Layout><PoOrders /></Layout>} />
            <Route path="po/new" element={<Layout><PoEditor /></Layout>} />
            <Route path="po/templates" element={<Layout><PoTemplates /></Layout>} />
            <Route path="po/supplier" element={<Layout><PoSupplier /></Layout>} />
            <Route path="po/:id" element={<Layout><PoEditor /></Layout>} />
          </>
        )}
      </Routes>
    </QdAuthContext.Provider>
  );
}
